import type { AnalyticsSink } from "@yjsgames/analytics-ports";
import { GPGS_ACHIEVEMENTS } from "../data/gpgsData.js";
import { resolveGpgsActions, type GpgsDefs } from "./resolveGpgsActions.js";
import type { GpgsPort } from "@yjsgames/gpgs-ports";

/**
 * GPGS 업적 sink 데코레이터 — 모든 분석 이벤트를 inner sink로 위임한 뒤, 그 이벤트가 매핑된 업적
 * 해금/증분을 GpgsPort로 실행한다(`PlatformTaggingSink` 패턴과 동형, 투명).
 *
 * ⚠ 리더보드 점수 제출은 본 sink가 아니라 `leaderboard/ChapterLeaderboardSink`(@yjsgames/leaderboard,
 * 플랫폼 무관 GPGS/Game Center)가 전담한다 — 중복 제출 방지를 위해 여기 defs.leaderboards는 비운다.
 *
 * 게임 로직은 이미 발화하는 분석 이벤트(chapter_clear/grid_unlock/...)만 알고 GPGS를 모른다 — 결합 0.
 * port는 getter 주입이라 네이티브 어댑터가 dynamic import로 교체돼도(createGpgs.onNativeReady) 반영된다.
 * GpgsPort 메서드는 자체 graceful(throw 안 함)이라 sink는 결과를 기다리지 않는다(fire-and-forget).
 */
export class GpgsAchievementSink implements AnalyticsSink {
  private readonly defs: GpgsDefs = {
    achievements: GPGS_ACHIEVEMENTS,
    leaderboards: [], // 리더보드는 ChapterLeaderboardSink가 전담(중복 제출 방지)
  };

  constructor(
    private readonly inner: AnalyticsSink,
    private readonly port: () => GpgsPort,
  ) {}

  log(event: string, params?: Record<string, string | number>): void {
    this.inner.log(event, params);
    const actions = resolveGpgsActions(event, params, this.defs);
    if (actions.length === 0) return;
    const port = this.port();
    for (const a of actions) {
      switch (a.type) {
        case "unlock":
          void port.unlockAchievement(a.achievementId);
          break;
        case "increment":
          void port.incrementAchievement(a.achievementId, a.steps);
          break;
        case "submitScore":
          void port.submitLeaderboardScore(a.leaderboardId, a.score);
          break;
      }
    }
  }
}
